import React from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Row, Col } from "react-bootstrap";
import Hr from "components/atoms/Hr/Hr";
import CopyToClipboard from "components/molecules/CopyToClipboard/CopyToClipboard";
import CardNew from "components/molecules/Card/CardNew";
import AddressLocodeAndCustomFields from "components/organisms/AddressLocodeAndCustomFields/AddressLocodeAndCustomFields";

const QuickSchedule = function ({ data, loadingData, isDrawer }) {
  const {
    pickup_contact_name,
    pickup_contact_phone,
    pickup_contact_email,
    pickup_address_name,
    pickup_datetime,
    pickup_notes,
    pickup_end_datetime,
    pickup_hours_start,
    pickup_hours_end,
    stop1_address_name,
    stop1_contact_name,
    stop1_contact_phone,
    stop1_contact_email,
    stop1_notes,
    stop1_datetime,
    stop1_end_datetime,
    stop1_hours_start,
    stop1_hours_end,
    address_v2,
  } = data?.schedule || {};

  const pickup_name = address_v2?.pickup_name ?? pickup_address_name;
  const pickup_street = address_v2?.pickup_street_address ?? data?.schedule?.pickup_street;
  const pickup_street_line_two = address_v2?.pickup_street_address_line_two ?? data?.schedule?.pickup_street_line_two;
  const pickup_city = address_v2?.pickup_city ?? data?.schedule?.pickup_city;
  const pickup_state = address_v2?.pickup_state ?? data?.schedule?.pickup_state;
  const pickup_country = address_v2?.pickup_country ?? data?.schedule?.pickup_country;
  const pickup_zipcode = address_v2?.pickup_zipcode ?? data?.schedule?.pickup_zipcode;

  const stop1_name = address_v2?.delivery_name ?? stop1_address_name;
  const stop1_street = address_v2?.delivery_street_address ?? data?.schedule?.stop1_street;
  const stop1_street_line_two = address_v2?.delivery_street_address_line_two ?? data?.schedule?.stop1_street_line_two;
  const stop1_city = address_v2?.delivery_city ?? data?.schedule?.stop1_city;
  const stop1_state = address_v2?.delivery_state ?? data?.schedule?.stop1_state;
  const stop1_country = address_v2?.delivery_country ?? data?.schedule?.stop1_country;
  const stop1_zipcode = address_v2?.delivery_zipcode ?? data?.schedule?.stop1_zipcode;

  const {
    pickup_location_reference_number,
    pickup_custom_address_reference,
    pickup_un_locode,
    delivery_location_reference_number,
    delivery_custom_address_reference,
    delivery_un_locode,
  } = address_v2 ?? {};

  return (
    <CardNew
      className={`${isDrawer ? "" : "mb-15"} font-b3`}
      isLoading={loadingData}
      title={<FormattedMessage id="quote-request-detail.schedule" defaultMessage="Schedule" />}
    >
      <Row className="mb-2 ">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.pickupCompany" defaultMessage="Pickup company" />
        </Col>
        <Col>{pickup_name}</Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quick-request-detail.schedule.pickupContact" defaultMessage="Pickup contact" />
        </Col>
        <Col>{pickup_contact_name}</Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quick-request-detail.schedule.pickupContactPhone" defaultMessage="Pickup contact phone" />
        </Col>
        <Col>
          <CopyToClipboard text={pickup_contact_phone} />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quick-request-detail.schedule.pickupContactEmail" defaultMessage="Pickup contact email" />
        </Col>
        <Col>
          <CopyToClipboard text={pickup_contact_email} />
        </Col>
      </Row>
      {(pickup_address_name || pickup_street) && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.pickupAddress" defaultMessage="Pickup address" />
          </Col>
          <Col>
            <CopyToClipboard text={pickup_street} />
            <CopyToClipboard text={pickup_street_line_two} />
            <AddressLocodeAndCustomFields
              un_locode={pickup_un_locode}
              location_reference_number={pickup_location_reference_number}
              custom_address_reference={pickup_custom_address_reference}
            />
          </Col>
        </Row>
      )}
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.pickupCityAndPostalCode" defaultMessage="Pickup city and postal code" />
        </Col>
        <Col>
          <CopyToClipboard text={`${pickup_city}${pickup_zipcode && `, ${pickup_zipcode}`}`} />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.pickupStateAndCountry" defaultMessage="Pickup state and country" />
        </Col>
        <Col>
          {pickup_state ? `${pickup_state}, ` : ""} {pickup_country}
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.pickupReadyDateTime" defaultMessage="Pickup ready date and time" />
        </Col>
        <Col>
          {pickup_end_datetime ? `${pickup_datetime} till ${pickup_end_datetime}` : pickup_datetime}
          {pickup_hours_start &&
            pickup_hours_end &&
            ` ${moment(pickup_hours_start, "HH:mm:ss").format("HH:mm")} - ${moment(pickup_hours_end, "HH:mm:ss").format(
              "HH:mm",
            )}`}
        </Col>
      </Row>
      {pickup_notes && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quick-request-detail.schedule.pickupNote" defaultMessage="Pickup note" />
          </Col>
          <Col>{pickup_notes || "-"}</Col>
        </Row>
      )}
      <Hr />
      <Row className="mb-2 ">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.deliveryCompany" defaultMessage="Delivery company" />
        </Col>
        <Col>{stop1_name}</Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quick-request-detail.schedule.deliveryContact" defaultMessage="Delivery contact" />
        </Col>
        <Col>{stop1_contact_name}</Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage
            id="quick-request-detail.schedule.deliveryContactPhone"
            defaultMessage="Delivery contact phone"
          />
        </Col>
        <Col>
          <CopyToClipboard text={stop1_contact_phone} />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage
            id="quick-request-detail.schedule.DeliveryContactEmail"
            defaultMessage="Delivery contact email"
          />
        </Col>
        <Col>
          <CopyToClipboard text={stop1_contact_email} />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.deliveryAddress" defaultMessage="Delivery address" />
        </Col>
        <Col>
          <CopyToClipboard text={stop1_street} />
          <CopyToClipboard text={stop1_street_line_two} />
          <AddressLocodeAndCustomFields
            un_locode={delivery_un_locode}
            location_reference_number={delivery_location_reference_number}
            custom_address_reference={delivery_custom_address_reference}
          />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage
            id="quote-request-detail.deliveryCityAndPostalCode"
            defaultMessage="Delivery city and postal code"
          />
        </Col>
        <Col>
          <CopyToClipboard text={`${stop1_city}${stop1_zipcode && `, ${stop1_zipcode}`}`} />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.deliveryStateAndCountry" defaultMessage="Delivery state and country" />
        </Col>
        <Col>
          {stop1_state ? `${stop1_state}, ` : ""} {stop1_country}
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage
            id="quote-request-detail.deliveryReadyDateAndTime"
            defaultMessage="Delivery ready date and time"
          />
        </Col>
        <Col>
          {stop1_end_datetime ? `${stop1_datetime} till ${stop1_end_datetime}` : stop1_datetime}
          {stop1_hours_start &&
            stop1_hours_end &&
            ` ${moment(stop1_hours_start, "HH:mm:ss").format("HH:mm")} - ${moment(stop1_hours_end, "HH:mm:ss").format(
              "HH:mm",
            )}`}
        </Col>
      </Row>
      {stop1_notes && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quick-request-detail.schedule.deliveryNote" defaultMessage="Delivery note" />
          </Col>
          <Col>{stop1_notes || "-"}</Col>
        </Row>
      )}
    </CardNew>
  );
};

QuickSchedule.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  loadingData: PropTypes.bool.isRequired,
  isDrawer: PropTypes.bool,
};

QuickSchedule.defaultProps = {
  isDrawer: false,
};

export default QuickSchedule;
